import React, { useState, useEffect } from 'react';

import './Devices.subtype.scss';

import Modal from 'react-modal';
import { useQuery, useLazyQuery } from '@apollo/client';
import gqlSchemaLibrary from '../services/graphql.schemas';
import DevicesBaseDevice from './Devices.device';
import ButtonIcon from '../forms/buttons/Button.icon';
import DevicesBaseAssign from './Devices.assign';
import DropdownText from '../forms/dropdown/dropdown.text';
import SpinnerLoader from "../visuals/SpinnerLoader";
import {useTranslation} from "react-i18next";
import DevicesBaseUnAssign from "./Devices.unAssign";
import {PageViewLog} from "../services/interactions/page.view";
import AuditTrail, {PAGES} from "../services/interactions/audit.trail";
import {ModalLog} from "../services/interactions/elements/modal";
import {useParams} from "react-router-dom";

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: "none",
        padding: "0px",
        boxShadow: "0 3px 8px 0 rgba(0, 0, 0, 0.38)",
    }
};

Modal.setAppElement('#root');

const _Modal = ModalLog(Modal);

function DevicesSubtype({ currentOrg, match, currentUser, toaster }) {
    let {devicesubtype} = useParams();
    const {t} = useTranslation(['devices','common']);
    const [peripherals, setPeripherals] = useState({ devices: [] });
    const [loadData, setLoadData] = useState(true);
    const [patientList, setPatientList] = useState({ patients: [] });
    const [patientsLoading, setPatientsLoading] = useState(true);

    const [searchFocused, changeSearchFocused] = useState(false);
    const [search, changeSearch] = useState('');
    const [deviceFilter, setDeviceFilter] = useState(null);
    const [sortAscending, setSortAscending] = useState(true);
    const [sorting, setSorting] = useState('patient');

    // Modal operations
    const [deviceToAssign, setDeviceToAssign] = useState(null);
    const [deviceToUnAssign, setDeviceToUnAssign] = useState(null);

    // Navigate operations
    const deviceSubtype = devicesubtype ? devicesubtype.split('_').join(' ') : null;

    useQuery(gqlSchemaLibrary.devices.peripherial.getPeripherialsByOrg, {
        variables: {
            deviceSubtype: deviceSubtype
        },
        onCompleted: (onDevices) => {
            if (onDevices.getPeripherialsByOrg) {
                console.log('Existing devices: ', onDevices.getPeripherialsByOrg);
                setPeripherals({ devices: onDevices.getPeripherialsByOrg.filter(d => d.deviceSubtype === deviceSubtype) });
            }
            setLoadData(false);
        },
        onError: () => {
            setLoadData(false);
        },
        fetchPolicy: 'cache-and-network'
    });

    const [fetchPatients] = useLazyQuery(gqlSchemaLibrary.patients.getPersonByOrganization, {
        onCompleted: ({ getPersonByOrganization }) => {
            console.log('[Patient List] User Patients: ', getPersonByOrganization);
            if (getPersonByOrganization) {
                setPatientList({ patients: getPersonByOrganization });
            }
            setPatientsLoading(false);
        },
        fetchPolicy: 'cache-and-network'
    });

    const [reFetchDevices] = useLazyQuery(gqlSchemaLibrary.devices.peripherial.getPeripherialsByOrg, {
        onCompleted: (onDevices) => {
            if (onDevices.getPeripherialsByOrg) {
                console.log('RE-FETCH Existing devices: ', onDevices.getPeripherialsByOrg);
                setPeripherals({ devices: onDevices.getPeripherialsByOrg.filter(d => d.deviceSubtype === deviceSubtype) });
            }
        },
        fetchPolicy: 'cache-and-network'
    });

    useEffect(() => {
        fetchPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFocus = (bool) => {
        return () => {
            AuditTrail.sendLog(PAGES.DEVICE_TYPE_LIST, {tag: 'input', event: 'onSearch', action: 'input search devices', focus: bool, value: search});
            changeSearchFocused(bool);
        }
    }

    const handleSearch = (e) => {
        const value = e.target.value;
        changeSearch(value);
    }

    const requestAssignDevice = (device) => {
        AuditTrail.sendLog(PAGES.DEVICE_TYPE_LIST, {tag: 'button', event: 'onClick', action: 'button open modal assigned device'});
        setDeviceToAssign(device);
    }

    const handleAssignSuccess = () => {
        reFetchDevices({
            variables: {
                deviceSubtype: deviceSubtype
            }
        });
        setDeviceToAssign(null);
        setDeviceToUnAssign(null);
    }

    const closeDeviceModal = () => {
        setDeviceToAssign(null);
        setDeviceToUnAssign(null);
    }

    const openDeviceFilter = (program) => {
        setDeviceFilter(program.value);
    }

    const onUnAssignDevice = (device) => {
        AuditTrail.sendLog(PAGES.DEVICE_TYPE_LIST, {tag: 'button', event: 'onClick', action: 'button open modal un-assigned device'});
        setDeviceToUnAssign(device);
    }

    const dropdownOptions = [
        { label: t('all-devices'), value: null },
        { label: t('available-devices'), value: 'available', color: '#2299f9' },
        { label: t('assigned-devices'), value: 'assigned' , color: '#f2cd01'},
        { label: t('in-use-devices'), value: 'isuse' , color: '#57c892'},
        { label: t('no-data-devices'), value: 'disabled' , color: 'transparent', borderColor: '#ff6a96' },
        { label: t('devices-not-kit'), value: 'not-kit' },
    ]

    const filterDevices = () => {
        if(deviceFilter === 'disabled') {
            return peripherals.devices.filter(d => !!d.patientId && !!d.dateUpdated && ((Date.now() - d.dateUpdated) >= (1000 * 60 * 60 * 24)));
        } else if(deviceFilter === 'isuse') {
            return peripherals.devices.filter(d => !!d.patientId && !!d.dateUpdated && ((Date.now() - d.dateUpdated) < (1000 * 60 * 60 * 0.5)));
        } else if(deviceFilter === 'assigned') {
            return peripherals.devices.filter(d => !!d.patientId && !d.dateUpdated);
        } else if (deviceFilter === 'available'){
            return peripherals.devices.filter(d => !d.patientId);
        } else if (deviceFilter === 'not-kit'){
            return peripherals.devices.filter(d => !d.kitId);
        } else {
            return peripherals.devices;
        }
    }

    const filterBySearch = (devicesFiltered) => {
        const patientCandidates = patientList.patients.map(p => ({_id: p._id, name: `${p.user.bio.firstname || ''} ${p.user.bio.lastname || ''}`.toLowerCase()}))
        .filter(({_id, name}) => name.includes(search.toLowerCase()))
        if(!search){
            return devicesFiltered;
        } else {
            return devicesFiltered.filter(d => {
                const kitIdMatch = (d.kitId || '').includes(search)
                const deviceIdMatch = (d.deviceId || '').includes(search)
                const patientIdMatch = patientCandidates.some(({_id}) => _id === d.patientId);
                return kitIdMatch || deviceIdMatch || patientIdMatch;
            })
        }
    }

    const renderAssignModal = () => {
        return (
            <_Modal
                isOpen={!!deviceToAssign}
                onRequestClose={closeDeviceModal}
                style={customStyles}
                contentLabel={t('assign-patch-device')}
                label="assign-patch-device"
                event={PAGES.DEVICE_TYPE_LIST}>
                {
                    deviceToAssign ?
                        <DevicesBaseAssign currentOrg={currentOrg} deviceToAssign={deviceToAssign} onClose={closeDeviceModal} onSucces={handleAssignSuccess} />
                        : ''
                }
            </_Modal>
        )
    }

    const renderUnAssignModal = () => {
        return (
            <_Modal
                isOpen={!!deviceToUnAssign}
                onRequestClose={closeDeviceModal}
                style={customStyles}
                contentLabel={t('assign-patch-device')}
                label="un-assign-patch-device"
                event={PAGES.DEVICE_TYPE_LIST}>
                {
                    deviceToUnAssign ?
                        <DevicesBaseUnAssign currentOrg={currentOrg} deviceToAssign={deviceToUnAssign} onClose={closeDeviceModal} onSucces={handleAssignSuccess} />
                        : ''
                }
            </_Modal>
        )
    }

    const sortList = (key) => {
        return () => {
            AuditTrail.sendLog(PAGES.DEVICE_TYPE_LIST, {tag: 'button', event: 'onClick', action: 'button toggle sort', sort: key, sort_ascending: !sortAscending});
            setSorting(key);
            setSortAscending(!sortAscending);
        }
    }

    // programIdFilter
    const devicesAfterFilter = filterBySearch(filterDevices()).sort((a, b) => {
        if (!sorting) {
            return true;
        }
        if (sorting === 'kitID') {
            const kitIDA = (a.kitId || 'z');
            const kitIDB = (b.kitId || 'z');
            if (sortAscending) {
                return kitIDA.localeCompare(kitIDB);
            } else {
                return kitIDB.localeCompare(kitIDA);
            }
        }
        if (sorting === 'kitID') {
            const kitIDA = (a.kitId || 'z');
            const kitIDB = (b.kitId || 'z');
            if (sortAscending) {
                return kitIDA.localeCompare(kitIDB);
            } else {
                return kitIDB.localeCompare(kitIDA);
            }
        }
        if (sorting === 'patient') {
            const patientAssignedA = patientList.patients.find(p => p._id === a.patientId);
            const patientBioA = !!patientAssignedA && !!patientAssignedA.user ? `${patientAssignedA.user.bio.lastname}, ${patientAssignedA.user.bio.firstname}` : 'z';
            const patientAssignedB = patientList.patients.find(p => p._id === b.patientId);
            const patientBioB = !!patientAssignedB && !!patientAssignedB.user ? `${patientAssignedB.user.bio.lastname}, ${patientAssignedB.user.bio.firstname}` : 'z';
            if (sortAscending) {
                return patientBioA.localeCompare(patientBioB);
            } else {
                return patientBioB.localeCompare(patientBioA);
            }
        }
        if (sorting === 'deviceID') {
            if (sortAscending) {
                return a.deviceId.localeCompare(b.deviceId);
            } else {
                return b.deviceId.localeCompare(a.deviceId);
            }
        }
        if (sorting === 'dateAssigned') {
            if(sortAscending){
                return (a.assignedAt > b.assignedAt)? 1: -1;
            }else{
                return (a.assignedAt > b.assignedAt)? -1: 1;
            }
        }
        return true;
    });

    const onBack = () => {
        AuditTrail.sendLog(PAGES.DEVICE_TYPE_LIST, {tag: 'button', event: 'onClick', action: 'button click back'});
        window.history.back();
    }

    return (
        <div id="DevicesSubtype">
            <div className="filter">
                <div className="filterOptions">
                    <i className="material-icons" onClick={onBack}>
                        arrow_back_ios
                    </i>
                    <div className="deviceTitle">
                        <h3>{deviceSubtype ? deviceSubtype : ''}</h3>
                    </div>
                </div>
                <div className="searchFilters">
                <DropdownText
                    transparent={true}
                    onChange={openDeviceFilter}
                    options={dropdownOptions}
                    label={t('filter-by-availability').toUpperCase()}
                    placeholder={t('filter-by-device')}
                    value={deviceFilter}
                    icon={true}
                    event={PAGES.DEVICE_TYPE_LIST}
                />
                    <div className="searchContainer">
                        <div className={`search ${searchFocused ? 'focused' : ''}`}>
                            <input value={search} placeholder={t('common:search')} onChange={handleSearch} onFocus={handleFocus(true)} onBlur={handleFocus(false)} />
                            <i className="material-icons">
                                search
                                </i>
                        </div>
                    </div>
                </div>

                <div className="addAction">
                    <p className="_counter">
                        {devicesAfterFilter.length} device{devicesAfterFilter.length === 1? '' : 's'}
                    </p>
                    <div className="enrollAction hidden">
                        <ButtonIcon>
                            <span className="material-icons">
                                add
                            </span>
                        </ButtonIcon>
                        <p>{t('new-device')}</p>
                    </div>
                </div>
            </div>
            <div className="devicesList scrollBar">
                <div className="headers">
                    <div className="selectable sortable centered">
                        <p onClick={sortList('kitID')}>
                            {t('kit-id')}
                        </p>
                        <div className="_updown" onClick={sortList('kitID')}>
                            <span className="_up material-icons">expand_less</span>
                            <span className="_down material-icons">expand_more</span>
                        </div>
                    </div>
                    <div className="selectable sortable centered">
                        <p onClick={sortList('deviceID')}>
                            {t('device-id')}
                        </p>
                        <div className="_updown" onClick={sortList('deviceID')}>
                            <span className="_up material-icons">expand_less</span>
                            <span className="_down material-icons">expand_more</span>
                        </div>
                    </div>
                    <div className="selectable sortable centered">
                        <p onClick={sortList('dateAssigned')}>
                            {t('date-assigned')}
                        </p>
                        <div className="_updown" onClick={sortList('dateAssigned')}>
                            <span className="_up material-icons">expand_less</span>
                            <span className="_down material-icons">expand_more</span>
                        </div>
                    </div>
                    <div className="selectable sortable centered">
                        <p onClick={sortList('patient')}>
                            {t('participant')}
                        </p>
                        <div className="_updown" onClick={sortList('patient')}>
                            <span className="_up material-icons">expand_less</span>
                            <span className="_down material-icons">expand_more</span>
                        </div>
                    </div>
                    <p>{t('status')}</p>
                    <p>{t('battery')}</p>
                    <div></div>
                </div>
                {
                    devicesAfterFilter.map((device) => (<DevicesBaseDevice currentUser={currentUser} loading={patientsLoading} patientList={patientList.patients} key={device._id} device={device} onAssign={requestAssignDevice} onUnAssignDevice={onUnAssignDevice} />))
                }
                { loadData? <SpinnerLoader/>: '' }
            </div>
            {renderAssignModal()}
            {renderUnAssignModal()}
        </div>
    )
}

export default PageViewLog(DevicesSubtype);
